import React, { useState } from "react";

// Simple, plain loading page: a single animated ring spinning around the
// app logo, with a short label beneath. One component, used in two
// places (post-login sync = fullscreen; login/signup = embedded). The
// `fullscreen` prop only controls whether it fills the viewport.
//
// Accessibility: respects prefers-reduced-motion (ring stops spinning);
// role=status + aria-live announces it to screen readers.
export default function ColdStartLoader({ h = {}, label = "Loading", fullscreen = false }) {
    const [logoFailed, setLogoFailed] = useState(false);

    const ac = h.ac || "#3b82f6";
    const bg = h.bg || "#f5f5f7";
    const tx = h.tx || "#111114";
    const sc = h.sc || "#8e8e93";
    const cardBg = h.cd || "#ffffff";
    const bd = h.bd || "rgba(0,0,0,0.06)";

    const container = fullscreen
        ? { position: "fixed", inset: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 100 }
        : { display: "flex", alignItems: "center", justifyContent: "center", padding: 40 };

    const RING = 104;   // spinning ring diameter
    const LOGO = 64;    // logo size inside

    return (
        <div style={container} role="status" aria-live="polite" aria-busy="true">
            <style>{`
                @keyframes csl-spin { to { transform: rotate(360deg); } }
                @keyframes csl-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
                @media (prefers-reduced-motion: reduce) {
                    .csl-spin { animation: none !important; }
                }
            `}</style>

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                {/* Ring + logo */}
                <div style={{ position: "relative", width: RING, height: RING, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {/* Track (faint full ring) */}
                    <div style={{
                        position: "absolute", inset: 0, borderRadius: "50%",
                        border: `3px solid ${ac}1A`,
                    }} />
                    {/* Spinning arc */}
                    <div className="csl-spin" style={{
                        position: "absolute", inset: 0, borderRadius: "50%",
                        border: "3px solid transparent",
                        borderTopColor: ac,
                        borderRightColor: ac,
                        animation: "csl-spin 0.9s linear infinite",
                    }} />
                    {/* Logo tile in the middle */}
                    <div style={{
                        width: LOGO, height: LOGO, borderRadius: 18,
                        background: logoFailed ? `linear-gradient(135deg, ${ac} 0%, #1d4ed8 100%)` : cardBg,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        overflow: "hidden",
                        boxShadow: `0 6px 18px ${ac}30`,
                        border: logoFailed ? "none" : `1px solid ${bd}`,
                    }}>
                        {logoFailed ? (
                            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="App logo">
                                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                <path d="M9 22V12h6v10" />
                            </svg>
                        ) : (
                            <img src="/logo192.png" alt="ProRentz" onError={() => setLogoFailed(true)}
                                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
                        )}
                    </div>
                </div>

                {/* Wordmark */}
                <div style={{ marginTop: 18, fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px", color: tx }}>
                    Pro<span style={{ color: ac }}>Rentz</span>
                </div>

                {/* Label */}
                <div className="csl-spin" style={{ marginTop: 6, fontSize: 13, fontWeight: 500, color: sc, animation: "csl-pulse 1.6s ease-in-out infinite" }}>
                    {label}…
                </div>
            </div>
        </div>
    );
}
